import { createSelector } from 'reselect';
import _ from 'lodash';

const IMAGE_KEYS = ['pdp_front_envelope', 'pdp_back_envelope', 'pdp_rsvp_envelope', 'pdp_envelope_options', 'pdp_postcard'];

export const getIsAdminView = state => state.cards.cardCatalog.isAdminView;

// SELECTORS FOR PLP
export const getCardCatalogBusy = state => state.cards.cardCatalog.busy;
export const getSuitesCount = state => state.cards.cardCatalog.suites.count;
export const getOrderedCardSuiteUUIDs = state => state.cards.cardCatalog.suites.orderedUUIDs;
export const getCardSuitesByUUID = state => state.cards.cardCatalog.suites.byUUID;
export const getSearchFilters = state => state.cards.cardCatalog.filters;

export const getCardSuitesAsArray = createSelector(
  getCardSuitesByUUID,
  getOrderedCardSuiteUUIDs,
  (cardSuitesByUUID, orderedCardSuiteUUIDs) => orderedCardSuiteUUIDs.map(uuid => cardSuitesByUUID[uuid])
);

export const getSuiteLeadCards = createSelector(
  getCardSuitesAsArray,
  cardSuites => cardSuites.map(cardSuite => ({ ...cardSuite.lead_card, suiteUUID: cardSuite.uuid, suiteName: cardSuite.name }))
);

export const getHasAnySearchFiltersEnabled = createSelector(
  getSearchFilters,
  searchFilters => (searchFilters.has_custom_photo || searchFilters.color !== null || searchFilters.has_foil)
);


// SELECTORS FOR PDP
export const getDetailSuite = state => state.cards.cardCatalog.detailSuite;
export const getLeadCard = state => state.cards.cardCatalog.detailSuite.leadCard;
export const getInitialVariationColor = state => state.cards.cardCatalog.initialVariationColor;
export const getOptionGroupsByKey = state => state.cards.cardCatalog.detailSuite.leadCard.optionGroupsByKey;
export const getThemeGroupsForCards = state => state.cards.cardCatalog.themeGroupsForCards;
export const getRelatedCardsFromLeadSuite = state => state.cards.cardCatalog.relatedCardsFromSuite;

export const getDetailSuiteName = createSelector(
  getDetailSuite,
  (detailSuite) => {
    if (detailSuite && detailSuite.name) {
      return detailSuite.name;
    }
    return null;
  }
);

export const getDetailSuiteDescription = createSelector(
  getDetailSuite,
  detailSuite => detailSuite.description
);

export const getDetailSuiteLeadCard = createSelector(
  getDetailSuite,
  detailSuite => detailSuite.leadCard
);

export const getLeadCardVariations = createSelector(
  getLeadCard,
  leadCard => leadCard.variations
);

export const getVariationOptions = createSelector(
  getDetailSuite,
  detailSuiteLeadCard => detailSuiteLeadCard.variationOptions
);

export const getVariationOptionsValues = createSelector(
  getVariationOptions,
  variationOptions => Object.keys(variationOptions).reduce((result, key) => {
    result[key] = variationOptions[key].value; // eslint-disable-line
    return result;
  }, {})
);

// export const getVariationOptionsV2 = state => state.cards.cardCatalog.detailSuite.variationOptions;

export const getActiveVariation = createSelector(
  getLeadCardVariations,
  getVariationOptions,
  (variations, variationOptions) => {
    // console.log('variations: ', variations);
    // console.log('variationOptions: ', variationOptions);
    const activeVariation = _.find(variations, {
      option_values: variationOptions
    });
    // console.log('activeVariation: ', activeVariation);
    // variations.forEach((variation) => {
    //   if (_.isEqual(variation.option_values, variationOptions)) {
    //     console.log('equal with variation options: ', variation);
    //   }
    // });
    return activeVariation;
  }
);

export const getVisibleActiveVariation = createSelector(
  getLeadCardVariations,
  getVariationOptions,
  getActiveVariation,
  (variations, variationOptions, activeVariation) => {
    // console.log('variationOptions: ', variationOptions);
    // console.log('variations: ', variations);
    // console.log('activeVariation: ', activeVariation);
    let visibleActiveVariation = activeVariation;
    if (variationOptions && variationOptions['reverse-printing']) {
      visibleActiveVariation = _.find(variations, {
        option_values: { ...variationOptions, 'reverse-printing': 'false' }
      }) || activeVariation;
      // return visibleActiveVariation;
    }
    // console.log('visibleActiveVariation: ', visibleActiveVariation);
    return visibleActiveVariation;
    // return _.find(variations, {
    //   option_values: { ...variationOptions }
    // });
  }
);

export const getActiveVariationImages = createSelector(
  getActiveVariation,
  (activeVariation) => {
    const activeVariationImages = IMAGE_KEYS.reduce((result, imageKey) => {
      if (activeVariation.image_urls[imageKey]) {
        result.push(activeVariation.image_urls[imageKey]);
      }
      return result;
    }, []);
    return activeVariationImages;
  }
);

export const getColorOptions = createSelector(
  getOptionGroupsByKey,
  (optionGroupsByKey) => {
    if (optionGroupsByKey.color && optionGroupsByKey.color.options) {
      return optionGroupsByKey.color.options;
    }
    return null;
  }
);

export const getActiveColorOptionLabel = createSelector(
  getColorOptions,
  getVariationOptions,
  (colorOptions, variationOptions) => {
    const color = _.find(colorOptions, { value: variationOptions.color });
    return color.label;
  }
);

export const getMinimumPriceCents = createSelector(
  getLeadCardVariations,
  (variations) => {
    const minPriceVariation = _.minBy(variations, variation => variation.price_cents);
    return minPriceVariation.price_cents;
  }
);

export const getActiveVariationPriceCents = createSelector(
  getActiveVariation,
  variation => variation.price_cents
);

export const getVisibleActiveVariationPriceCents = createSelector(
  getVisibleActiveVariation,
  variation => variation.sku.price_cents
);

export const getThemeGroupsForLeadCard = createSelector(
  getLeadCard,
  getThemeGroupsForCards,
  (leadCard, themeGroups) => {
    if (leadCard && themeGroups && themeGroups[leadCard.uuid]) {
      return themeGroups[leadCard.uuid];
    }
    return null;
  }
);

export const getRelatedCardsFromCurrentSuite = createSelector(
  getRelatedCardsFromLeadSuite,
  (relatedCardsFromSuite) => {
    if (relatedCardsFromSuite) {
      return relatedCardsFromSuite.map(relatedCardFromSuite => ({ ...relatedCardFromSuite.lead_card, suiteUUID: relatedCardFromSuite.uuid, suiteName: relatedCardFromSuite.name }));
    }
    return null;
  }
);

export const getCards = state => state.cards.cardCatalog.cards;



// WEBPACK FOOTER //
// ./src/cards/selectors/cardCatalogSelector.js